import { ValidateFields } from 'rc-field-form/es/interface';
import React, { useCallback, useContext, useMemo } from 'react';
import { IItemForGetShortInfoWithStatus, UserRoles, ITotalPrice, EntityStatus } from 'types';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import PriceField from './components/PriceField';
import AddNewItemBtn from './components/AddNewFieldButton';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { AppContext, IAppContext } from 'appState/appContext';
import style from './RestaurantPricesField.module.scss';

interface IRestaurantPricesField {
	addPrices: (value: ITotalPrice[]) => void;
	removePrice: (value: string[]) => void;
	setIDsIsForceDisabledRestaurant: (value: string) => void;
	setRestaurantsForPrice: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfoWithStatus[]) => void;
	changePrice: (value: string) => void;
	selectedCustomer: Nullable<string>;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
	IDsIsForceDisabledRestaurant: string[];
	price: string;
	externalId: string;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	revalidateForm: ValidateFields;
}

const RestaurantPricesField: React.FC<IRestaurantPricesField> = ({
	restaurantsForPrice,
	totalPrices,
	IDsIsForceDisabledRestaurant,
	fetchedRestaurants,
	price,
	selectedCustomer,
	externalId,
	setRestaurantsForPrice,
	setFetchedRestaurants,
	removePrice,
	setIDsIsForceDisabledRestaurant,
	addPrices,
	changePrice,
	revalidateForm,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const disabledButton = !restaurantsForPrice.length || !price || !!externalId;

	const restaurantsList = useMemo(() => {
		return totalPrices.map((el) => {
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			const toggleIsForceDisabledRestaurant = () => setIDsIsForceDisabledRestaurant(el.restaurantId);

			return (
				<div className={style.selectedItemWrapper} key={el.restaurantId}>
					<SelectedItem
						className={style.selectedItem}
						label={[el.restaurantName, el.price]}
						onDelete={
							!!externalId || !(hasAccess || accessibleRestaurantIds?.includes(el.restaurantId))
								? undefined
								: () => removePrice([el.restaurantId])
						}
					/>
					<Checkbox
						className={style.checkboxOfSelectedItem}
						checked={IDsIsForceDisabledRestaurant.includes(el.restaurantId)}
						onChange={toggleIsForceDisabledRestaurant}
						disabled={!hasAccess && !accessibleRestaurantIds?.includes(el.restaurantId)}
						data-test="isForceDisabledCheckbox">
						<Text translationNameSpace="form" translationKey="isForceDisabledLabel" withTooltip={true} />
					</Checkbox>
				</div>
			);
		});
	}, [totalPrices, IDsIsForceDisabledRestaurant, loggedUser, externalId, hasAccess, removePrice]);

	const handleAddPrice = useCallback(() => {
		const prices: ITotalPrice[] = restaurantsForPrice.map((el) => {
			const fetchedRestaurant = fetchedRestaurants.find((rest) => rest.id === el);
			return {
				restaurantId: el,
				restaurantName: fetchedRestaurant?.name || '',
				restaurantStatus: fetchedRestaurant?.status || EntityStatus.Active,
				price,
			};
		});
		prices.forEach((el) => {
			if (!totalPrices.some((total) => total.restaurantId === el.restaurantId)) {
				addPrices([el]);
				revalidateForm();
			}
		});
	}, [restaurantsForPrice, fetchedRestaurants, price, totalPrices, addPrices]);

	return (
		<div className={style.container} data-test="RestaurantPricesField">
			<div className={style.field}>
				<RestaurantsMultipleSelect
					restaurantsForPrice={restaurantsForPrice}
					totalPrices={totalPrices}
					selectedCustomer={selectedCustomer}
					externalId={externalId}
					setRestaurantsForPrice={setRestaurantsForPrice}
					removePrice={removePrice}
					setFetchedRestaurants={setFetchedRestaurants}
				/>
				<PriceField
					externalId={externalId}
					restaurantsForPrice={restaurantsForPrice}
					changePrice={changePrice}
				/>
				<AddNewItemBtn onClick={handleAddPrice} isDisabled={disabledButton} />
			</div>
			<div className={style.pricesList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantPricesField);
